import apiClient from './client';

/**
 * POST /expenses with optional Idempotency-Key header.
 * Body matches ExpenseCreate (expense_name, date, amount, expense_type, category, vendor, ...)
 */
export async function createExpense(payload, { idempotencyKey } = {}) {
  const headers = {};
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
  const response = await apiClient.post('/expenses', payload, { headers });
  return response.data;
}

/**
 * POST /expenses/{id}/document — multipart upload of a receipt.
 * The backend converts images to PDF on its side; we send the original blob.
 */
export async function uploadExpenseDocument(expenseId, fileBlob, filename, { idempotencyKey } = {}) {
  const form = new FormData();
  form.append('file', fileBlob, filename);
  const headers = { 'Content-Type': 'multipart/form-data' };
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
  const response = await apiClient.post(
    `/expenses/${expenseId}/document`,
    form,
    { headers },
  );
  return response.data;
}

/**
 * GET /expenses/zimra-lookup?url={qrUrl}
 *
 * Server-side proxy that fetches the ZIMRA verification page (CORS
 * isn't friendly to direct browser fetches) and parses the relevant
 * fields out of the HTML. Used after a successful QR scan to pre-fill
 * the capture form with vendor / amount / date / fiscal number.
 *
 * Returns null if the lookup fails for any reason — the caller is
 * expected to fall back gracefully (rep just types the fields
 * manually). We deliberately don't surface a hard error: a partial
 * QR-only flow is still a better UX than blocking the rep on a
 * network hiccup.
 *
 * Response shape (when successful):
 *   {
 *     valid: bool,
 *     taxpayer_name: str?,
 *     trade_name: str?,
 *     address: str?,
 *     invoice_number: str?,        // canonical fiscal / invoice number
 *     invoice_date: str?,          // ISO date (no time)
 *     invoice_date_raw: str?,      // "DD/MM/YYYY HH:MM:SS"
 *     amount: number?,             // gross
 *     currency: str?,              // "USD" by default
 *     fiscal_number: str?,         // alias of invoice_number
 *     device_id: str?,
 *     raw: { … original label/value map … },
 *   }
 */
export async function lookupZimraInvoice(qrUrl) {
  try {
    const response = await apiClient.get('/expenses/zimra-lookup', {
      params: { url: qrUrl },
      // Slightly above the backend's 10s httpx timeout so we surface
      // the backend's specific error rather than a generic frontend
      // timeout.
      timeout: 12_000,
    });
    return response.data;
  } catch (err) {
    // Swallow — caller falls back to manual entry. Log for diagnostics.
    // eslint-disable-next-line no-console
    console.warn('ZIMRA lookup failed:', err?.response?.data?.detail || err?.message || err);
    return null;
  }
}

// Top-level expense type. Backend `expense_type` field — these three are
// the canonical values from backend/app/schemas/expense.py:6
// (cogs / operating / vat_payment). The mobile capture form picks one
// of these instead of a fine-grained category, on the basis that reps
// in the field know "this is an op-ex" / "this is COGS" but rarely
// know the right specific category bucket. Detail-level categorisation
// stays on the desktop ERP.
export const EXPENSE_TYPES = [
  { value: 'operating',   label: 'Operating expense' },
  { value: 'cogs',        label: 'Cost of sales' },
  { value: 'vat_payment', label: 'VAT payment' },
];

// Payment method. Backend `payment_type` field is a free-text string
// (no enum validation) but desktop and ledger reports key on these
// canonical values, so we keep mobile in sync.
export const PAYMENT_TYPES = [
  { value: 'cash',          label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'card',          label: 'Card' },
  { value: 'eft',           label: 'EFT' },
  { value: 'cheque',        label: 'Cheque' },
];
